/**
 * Record every fetch() on staging, not just the ones going through apiRequest.
 *
 * The debug panel originally tapped apiRequest, which most of the app uses --
 * but several pages call fetch() directly, and those were invisible in the
 * panel exactly when something was failing. A 500 on such a page left nothing
 * to look at on a phone.
 *
 * Patching fetch itself catches everything regardless of how a page is
 * written. Staging only: DEBUG_ENABLED folds to false in production and the
 * whole module is dropped by the bundler.
 */
import { DEBUG_ENABLED, recordRequest } from './requestLog.js';

let installed = false;

export function installFetchTap() {
  if (!DEBUG_ENABLED || installed || typeof window === 'undefined') return;
  installed = true;

  const original = window.fetch;

  window.fetch = async function tapped(input, init) {
    const started = Date.now();
    const method = (init?.method || (typeof input === 'object' && input?.method) || 'GET').toUpperCase();
    const url = typeof input === 'string' ? input : input?.url || String(input);

    try {
      const response = await original.call(this, input, init);

      // Read the body from a clone so the caller still gets an unconsumed one.
      let body = '';
      try {
        body = await response.clone().text();
      } catch {
        body = '<body unavailable>';
      }

      recordRequest({
        method,
        url: shorten(url),
        status: response.status,
        ok: response.ok,
        ms: Date.now() - started,
        at: new Date().toISOString(),
        response: body.slice(0, 4000),
      });

      return response;
    } catch (error) {
      // Transport failures: no status, and the hardest to see on a phone.
      recordRequest({
        method,
        url: shorten(url),
        status: 0,
        ok: false,
        ms: Date.now() - started,
        at: new Date().toISOString(),
        response: `${error.name}: ${error.message}`,
      });
      throw error;
    }
  };
}

// The origin is the same for every row and eats the width on a phone.
function shorten(url) {
  try {
    const u = new URL(url, window.location.origin);
    return u.pathname + u.search;
  } catch {
    return url;
  }
}
